import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Bug, Sparkles, AlertTriangle, X, Send, Check,
  Monitor, Globe, ChevronUp, Loader2, MessageCircle,
  Mic, MicOff, User, Bot, Copy, Archive
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista de emails admin que podem ver o agente
const ADMIN_EMAILS = ['lu.756@icloud.com', 'lucienne.escritora@gmail.com'];

/**
 * Agente Humano Pontuais - Assistente conversacional para registro de bugs/melhorias
 * Aparece apenas para administradores em todas as páginas do app
 */
const HumanoPontuaisAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [issueData, setIssueData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users/me`, {
          withCredentials: true
        });
        const userEmail = response.data?.email?.toLowerCase() || '';
        setIsAdmin(ADMIN_EMAILS.some(e => e.toLowerCase() === userEmail));
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  // Initialize conversation when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const now = new Date();
      const greeting = `Olá! Sou o assistente de registro. 🐛

Estou aqui para ajudar você a documentar bugs, melhorias ou problemas.

**Página atual:** ${window.location.pathname}
**Data/Hora:** ${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}

Me conta: o que você observou ou gostaria de melhorar?`;
      
      setMessages([{ role: 'assistant', content: greeting, timestamp: now }]);
    }
  }, [isOpen, messages.length]);

  // Don't render if not admin
  if (!isAdmin) return null;

  // Collect technical data
  const collectTechnicalData = () => {
    return {
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      pathname: window.location.pathname
    };
  };

  // Send message to AI
  const handleSendMessage = async () => {
    if (!inputText.trim() || loading) return;

    const userMessage = inputText.trim();
    const now = new Date();
    
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage, 
      timestamp: now 
    }]);
    setInputText('');
    setLoading(true);

    try {
      // Call AI to analyze and respond
      const response = await axios.post(`${API_URL}/api/humano-pontuais/chat`, {
        message: userMessage,
        conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
        page_url: window.location.href,
        technical_data: collectTechnicalData()
      }, { withCredentials: true });

      const aiResponse = response.data;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse.message,
        timestamp: new Date()
      }]);

      // If AI determined we have enough info, save the issue data
      if (aiResponse.issue_ready) {
        setIssueData({
          type: aiResponse.issue_type || 'bug',
          description: aiResponse.issue_summary,
          conversation: [...messages, { role: 'user', content: userMessage }],
          technical_data: collectTechnicalData()
        });
      }
    } catch (error) {
      // Fallback response if AI fails
      const fallbackResponse = analyzeLocally(userMessage);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: fallbackResponse.message,
        timestamp: new Date()
      }]);
      
      if (fallbackResponse.issue_ready) {
        setIssueData({
          type: fallbackResponse.issue_type,
          description: fallbackResponse.issue_summary,
          conversation: [...messages, { role: 'user', content: userMessage }],
          technical_data: collectTechnicalData()
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Local analysis fallback
  const analyzeLocally = (message) => {
    const lowerMsg = message.toLowerCase();
    
    // Detect issue type
    let type = 'melhoria';
    if (lowerMsg.includes('bug') || lowerMsg.includes('erro') || lowerMsg.includes('quebr') || 
        lowerMsg.includes('não funciona') || lowerMsg.includes('sumiu') || lowerMsg.includes('travou')) {
      type = 'bug';
    } else if (lowerMsg.includes('problema') || lowerMsg.includes('lento') || lowerMsg.includes('difícil')) {
      type = 'problema';
    }

    // Check if we have enough info
    const hasEnoughInfo = message.length > 20;

    if (hasEnoughInfo) {
      const typeEmoji = type === 'bug' ? '🐛' : type === 'melhoria' ? '✨' : '⚠️';
      return {
        message: `Entendi! Vou registrar como **${type.toUpperCase()}** ${typeEmoji}

**Resumo:**
${message}

**Página:** ${window.location.pathname}
**Data:** ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}

Está correto? Posso registrar assim ou quer adicionar mais detalhes?

Clique em **"Registrar"** quando estiver pronto, ou continue descrevendo.`,
        issue_ready: true,
        issue_type: type,
        issue_summary: message
      };
    } else {
      return {
        message: `Pode me dar mais detalhes? 

Por exemplo:
- O que você estava fazendo quando aconteceu?
- O que esperava que acontecesse?
- O que aconteceu de diferente?`,
        issue_ready: false
      };
    }
  };

  // Save issue
  const handleSaveIssue = async () => {
    if (!issueData) return;

    setIsSaving(true);
    try {
      // Build full conversation as description
      const conversationText = messages
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join('\n\n');

      await axios.post(`${API_URL}/api/humano-pontuais/issues`, {
        type: issueData.type,
        description: issueData.description || conversationText,
        page_url: window.location.href,
        technical_data: {
          ...issueData.technical_data,
          conversation_summary: messages.slice(0, 10).map(m => ({
            role: m.role,
            content: m.content.substring(0, 200),
            time: m.timestamp?.toISOString()
          }))
        }
      }, { withCredentials: true });

      toast.success('Registrado com sucesso! Veja no /saudeapp');
      
      // Reset and close
      setTimeout(() => {
        setMessages([]);
        setIssueData(null);
        setIsOpen(false);
      }, 1000);
    } catch (error) {
      toast.error('Erro ao registrar');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Type config
  const getTypeConfig = (type) => {
    switch (type) {
      case 'bug': return { icon: <Bug className="w-4 h-4" />, color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'melhoria': return { icon: <Sparkles className="w-4 h-4" />, color: 'text-amber-400', bg: 'bg-amber-500/20' };
      case 'problema': return { icon: <AlertTriangle className="w-4 h-4" />, color: 'text-orange-400', bg: 'bg-orange-500/20' };
      default: return { icon: <Bug className="w-4 h-4" />, color: 'text-stone-400', bg: 'bg-stone-500/20' };
    }
  };

  return (
    <>
      {/* Botão flutuante */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
          isOpen 
            ? 'bg-stone-700 rotate-180' 
            : 'bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 hover:scale-110'
        }`}
        title="Assistente de Registro"
        data-testid="humano-pontuais-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <div className="relative">
            <Bug className="w-6 h-6 text-white" />
            <MessageCircle className="w-3 h-3 text-white absolute -top-1 -right-1" />
          </div>
        )}
      </button>

      {/* Chat modal */}
      {isOpen && (
        <div 
          className="fixed bottom-44 right-6 z-50 w-96 h-[500px] bg-stone-900 rounded-2xl shadow-2xl border border-stone-700 overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-200"
          data-testid="humano-pontuais-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500/20 to-pink-500/20 px-4 py-3 border-b border-stone-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                  <Bug className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="font-medium text-white text-sm">Assistente de Registro</span>
                  <p className="text-xs text-stone-400">Bugs, Melhorias e Problemas</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-stone-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div 
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-md' 
                      : 'bg-stone-800 text-stone-200 rounded-bl-md'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap" 
                    dangerouslySetInnerHTML={{ 
                      __html: msg.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>') 
                    }} 
                  />
                  <div className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-stone-500'}`}>
                    {formatTime(msg.timestamp)}
                  </div>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-stone-800 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Issue ready banner */}
          {issueData && (
            <div className="px-4 py-2 bg-green-500/10 border-t border-green-500/30 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getTypeConfig(issueData.type).icon}
                  <span className="text-sm text-green-400">Pronto para registrar</span>
                </div>
                <Button
                  onClick={handleSaveIssue}
                  disabled={isSaving}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Registrar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="p-3 border-t border-stone-700 flex-shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Descreva o que observou..."
                className="flex-1 bg-stone-800 border border-stone-700 rounded-full px-4 py-2 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-rose-500"
                disabled={loading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || loading}
                className="w-10 h-10 rounded-full bg-rose-500 hover:bg-rose-600 disabled:bg-stone-700 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HumanoPontuaisAgent;
